import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Save, Loader2, CheckCircle, CalendarDays,
  Plus, Edit, Trash2, Clock, Mail, Send, Users, Bell, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import SiteAnnouncementBanner from "@/components/SiteAnnouncementBanner";
import { EventFormModal } from "@/components/EventFormModal";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { BusinessProfileForm } from "@/components/forms/BusinessProfileForm";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { EmailLogDialog } from "@/components/EmailLogDialog";
import { EmailBetaOverlay } from "@/components/EmailBetaOverlay";
import { AnnouncementsPanel } from "@/components/AnnouncementsPanel";
import { normalizeUrlFields } from "@/lib/urlUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatEmailLogDate = (value) => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getLogoUrl = (logo) => {
  if (!logo) return "";
  if (logo.startsWith("http")) return logo;
  return `${BACKEND_URL}${logo}`;
};

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;

  if (!detail) return fallback;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.msg) {
          const field = Array.isArray(item.loc)
            ? item.loc.filter((part) => part !== "body").join(".")
            : "";
          return field ? `${field}: ${item.msg}` : item.msg;
        }
        return null;
      })
      .filter(Boolean);

    return messages.length ? messages.join("; ") : fallback;
  }

  return detail?.msg || fallback;
};

const formatEventTimeRange = (event) => {
  if (event.time && event.end_time) return `${event.time} - ${event.end_time}`;
  return event.time || "";
};

const US_STATE_PATTERN = "(?:A[LKSZR]|C[AOT]|D[CE]|F[LM]|G[AU]|HI|I[ADLN]|K[SY]|LA|M[ADEHINOST]|N[CDEHJMVY]|O[HKR]|P[AWR]|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY]|DC|Alabama|Alaska|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|Florida|Georgia|Hawaii|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New Hampshire|New Jersey|New Mexico|New York|North Carolina|North Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode Island|South Carolina|South Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West Virginia|Wisconsin|Wyoming|District of Columbia)";

const isValidAddress = (value) => {
  const text = String(value || "").trim().replace(/\s+/g, " ");
  const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
  const streetPart = parts[0] || text;
  const hasStreet = /\b\d{1,6}\b/.test(streetPart) && /[a-z]/i.test(streetPart);
  const hasState = new RegExp(`\\b${US_STATE_PATTERN}\\b`, "i").test(text);
  const hasZip = /\b\d{5}(?:-\d{4})?\b/.test(text);
  const hasCity = parts.length >= 3 || new RegExp(`[A-Za-z][A-Za-z .'-]{2,}\\s+${US_STATE_PATTERN}\\s+\\d{5}`, "i").test(text);
  return hasStreet && hasCity && hasState && hasZip;
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const token = user?.token;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [dashboardTab, setDashboardTab] = useState("listing");
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [addingBusiness, setAddingBusiness] = useState(false);
  const [marketOptions, setMarketOptions] = useState([]);
  const [business, setBusiness] = useState({
    business_name: "",
    business_email: "",
    instagram: "",
    facebook: "",
    tiktok: "",
    business_type: "",
    business_categories: [],
    business_category_other: "",
    address: "",
    business_addresses: [],
    additional_business_locations: [],
    is_home_based: false,
    website: "",
    hours: "",
    business_description: "",
    location: "",
    business_locations: [],
    phone_number: "",
    logo: "",
    is_farmers_market_vendor: false,
    farmers_market_vendor_market_ids: [],
    market_vendor_requests: [],
  });
  const [myEvents, setMyEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [emailList, setEmailList] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailDraft, setEmailDraft] = useState(null);
  const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [selectedEmailLog, setSelectedEmailLog] = useState(null);
  const [addressErrorIndexes, setAddressErrorIndexes] = useState([]);

  const selectedBusinessQuery = selectedBusinessId ? `?business_id=${encodeURIComponent(selectedBusinessId)}` : "";
  const selectedEventQuery = selectedBusinessId ? `?host_id=${encodeURIComponent(selectedBusinessId)}` : "";

  const fetchBusiness = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/businesses/mine`, { headers: { Authorization: `Bearer ${token}` } });
      const listings = r.data || [];
      setBusinesses(listings);
      if (listings.length) {
        const nextSelectedId = selectedBusinessId && listings.some((listing) => listing.id === selectedBusinessId)
          ? selectedBusinessId
          : listings[0].id;
        setSelectedBusinessId(nextSelectedId);
        setBusiness(listings.find((listing) => listing.id === nextSelectedId) || listings[0]);
      } else {
        const fallback = await axios.get(`${API}/business`, { headers: { Authorization: `Bearer ${token}` } });
        setBusiness(fallback.data);
      }
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally { setLoading(false); }
  }, [token, logout, selectedBusinessId]);

  const fetchMyEvents = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events/my${selectedEventQuery}`, { headers: { Authorization: `Bearer ${token}` } });
      setMyEvents(r.data);
    } catch { /* ignore */ }
  }, [token, selectedEventQuery]);

  const fetchEmailList = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [listResponse, logResponse, draftResponse] = await Promise.all([
        axios.get(`${API}/business/email-list${selectedBusinessQuery}`, { headers }),
        axios.get(`${API}/business/email-list/sends${selectedBusinessQuery}`, { headers }),
        axios.get(`${API}/business/email-list/draft${selectedBusinessQuery}`, { headers }),
      ]);
      setEmailList(listResponse.data);
      setEmailLogs(logResponse.data || []);
      const draft = draftResponse.data || null;
      setEmailDraft(draft);
      setEmailSubject(draft?.subject || "");
      setEmailMessage(draft?.message || "");
    } catch { /* ignore until the profile is fully available */ }
  }, [token, selectedBusinessQuery]);

  const fetchMarketOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/markets/directory`);
      setMarketOptions(response.data || []);
    } catch {
      setMarketOptions([]);
    }
  }, []);

  const fetchAnnouncementCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/announcements`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncementUnreadCount(response.data.unread_count || 0);
    } catch { /* announcements are optional on dashboard load */ }
  }, [token]);

  useEffect(() => { fetchBusiness(); }, [fetchBusiness]);
  useEffect(() => {
    if (!token || loading) return;
    fetchMyEvents();
    fetchEmailList();
    fetchAnnouncementCount();
    fetchMarketOptions();
  }, [token, loading, selectedBusinessId, fetchMyEvents, fetchEmailList, fetchAnnouncementCount, fetchMarketOptions]);

  const handleBusinessSwitch = (businessId) => {
    const nextBusiness = businesses.find((listing) => listing.id === businessId);
    if (!nextBusiness) return;
    setSelectedBusinessId(businessId);
    setBusiness(nextBusiness);
    setHasChanges(false);
    setEmailList(null);
    setEmailLogs([]);
    setEmailDraft(null);
    setEmailSubject("");
    setEmailMessage("");
  };

  const handleAddBusinessListing = async () => {
    setAddingBusiness(true);
    try {
      const response = await axios.post(`${API}/businesses/mine`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const nextBusiness = response.data;
      setBusinesses((previous) => [nextBusiness, ...previous]);
      setSelectedBusinessId(nextBusiness.id);
      setBusiness(nextBusiness);
      setDashboardTab("listing");
      setHasChanges(true);
      toast.success("New business listing added");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to add business listing"));
    } finally {
      setAddingBusiness(false);
    }
  };

  const handleChange = (field, value) => {
    setBusiness((p) => ({ ...p, [field]: value }));
    setAddressErrorIndexes([]);
    setHasChanges(true);
  };

  const handleSave = async () => {
    const locations = Array.isArray(business.business_locations) ? business.business_locations.filter(Boolean) : [];
    const addresses = Array.isArray(business.business_addresses) ? business.business_addresses : [];
    if (locations.length === 0) {
      toast.error("Select at least one city or region");
      return;
    }
    if (business.is_home_based && locations.length > 1) {
      toast.error("Home-based businesses can only select one city or region");
      return;
    }
    const requiredAddresses = business.is_home_based ? [] : locations.map((_, index) => addresses[index]);
    const invalidAddressIndexes = requiredAddresses
      .map((address, index) => isValidAddress(address) ? null : index)
      .filter((index) => index !== null);
    if (invalidAddressIndexes.length > 0) {
      setAddressErrorIndexes(invalidAddressIndexes);
      toast.error("Enter a full address with street, city, state, and ZIP for each selected city or region");
      return;
    }
    const additionalLocations = Array.isArray(business.additional_business_locations)
      ? business.additional_business_locations.filter((location) => location?.name || location?.address)
      : [];
    const hasColumbusLocation = locations.some((location) => location.toLowerCase() === "columbus");
    if (!business.is_home_based && hasColumbusLocation) {
      const invalidAdditionalLocation = additionalLocations.find((location) => !location?.name?.trim() || !isValidAddress(location?.address));
      if (invalidAdditionalLocation) {
        toast.error("Enter a name and full address for each additional business location");
        return;
      }
    }
    setAddressErrorIndexes([]);
    setSaving(true);
    try {
      const updateData = normalizeUrlFields({
        business_name: business.business_name,
        business_type: business.business_type,
        business_categories: business.business_categories || [],
        business_category_other: business.business_category_other || "",
        address: business.address,
        business_addresses: business.business_addresses || [],
        additional_business_locations: additionalLocations,
        is_home_based: !!business.is_home_based,
        website: business.website,
        hours: business.hours,
        business_description: business.business_description,
        location: business.location,
        business_locations: business.business_locations || [],
        phone_number: business.phone_number,
        logo: business.logo,
        is_farmers_market_vendor: !!business.is_farmers_market_vendor,
        farmers_market_vendor_market_ids: business.farmers_market_vendor_market_ids || [],
        instagram: business.instagram,
        facebook: business.facebook,
        tiktok: business.tiktok,
      });
      const response = await axios.put(`${API}/business${selectedBusinessQuery}`, updateData, { headers: { Authorization: `Bearer ${token}` } });
      const savedBusiness = response.data;
      setBusiness(savedBusiness);
      setBusinesses((previous) => previous.map((listing) => listing.id === savedBusiness.id ? savedBusiness : listing));
      toast.success("Changes saved");
      setHasChanges(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to save changes"));
    } finally { setSaving(false); }
  };

  const handleDeleteEvent = async (eventId) => {
    setDeletingEventId(eventId);
    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Event deleted");
      fetchMyEvents();
    } catch { toast.error("Failed to delete event"); }
    finally { setDeletingEventId(null); }
  };

  const handleSendMessage = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/business/email-list/send-message${selectedBusinessQuery}`, {
        subject: emailSubject,
        message: emailMessage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Email sent successfully");
      setEmailSubject("");
      setEmailMessage("");
      setEmailDraft(null);
      fetchEmailList();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to send email"));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSendingTestEmail(true);
    try {
      await axios.post(`${API}/business/email-list/send-test${selectedBusinessQuery}`, {
        subject: emailSubject,
        message: emailMessage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Test email sent to your inbox");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to send test email"));
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSaveEmailDraft = async () => {
    if (!emailSubject.trim() && !emailMessage.trim()) {
      toast.error("Add a subject or message before saving a draft");
      return;
    }
    setSavingDraft(true);
    try {
      const response = await axios.put(`${API}/business/email-list/draft${selectedBusinessQuery}`, {
        subject: emailSubject,
        message: emailMessage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEmailDraft(response.data);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to save draft"));
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDeleteEmailDraft = async () => {
    setSavingDraft(true);
    try {
      await axios.delete(`${API}/business/email-list/draft${selectedBusinessQuery}`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailDraft(null);
      toast.success("Draft deleted");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to delete draft"));
    } finally {
      setSavingDraft(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  const emailAccessEnabled = !!(business?.subscription_active || business?.admin_subscription_override || emailList?.email_access_enabled);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SiteAnnouncementBanner />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {business?.status === "pending" && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-yellow-200 bg-yellow-50 p-4" data-testid="business-pending-banner">
            <Clock className="h-5 w-5 flex-shrink-0 text-yellow-600" />
            <p className="text-sm text-yellow-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Your business listing is pending admin approval. This note will disappear once your listing is approved.
            </p>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Business Listing</h1>
            <p className="text-slate-500 mt-1">Manage your business information</p>
          </div>
          {dashboardTab === "listing" && (
            <Button onClick={handleSave} disabled={saving || !hasChanges} className="h-10 px-6 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold disabled:opacity-50" data-testid="save-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                : hasChanges ? <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                : <><CheckCircle className="w-4 h-4 mr-2" /> Saved</>}
            </Button>
          )}
        </div>

        {dashboardTab === "listing" && hasChanges && (
          <div className="fixed bottom-5 left-1/2 z-50 flex w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50/95 p-4 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:justify-between" data-testid="business-unsaved-changes-notice">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <p className="text-sm font-medium text-amber-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                You have unsaved profile changes.
              </p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="h-10 rounded-full bg-[#1a1a1a] px-5 font-bold text-white hover:bg-black disabled:opacity-50" data-testid="floating-business-save-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
            </Button>
          </div>
        )}

        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.12em] text-slate-400">Active business</p>
            <p className="mt-1 text-sm text-slate-500">Switch between the business listings connected to this email account.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={selectedBusinessId} onValueChange={handleBusinessSwitch}>
              {businesses.length === 0 ? (
                <SelectTrigger className="h-11 min-w-[16rem] rounded-full border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm" data-testid="business-listing-switcher">
                  <SelectValue placeholder="Current business" />
                </SelectTrigger>
              ) : (
                <>
                  <SelectTrigger className="h-11 min-w-[16rem] rounded-full border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm" data-testid="business-listing-switcher">
                    <SelectValue placeholder="Current business" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((listing) => (
                      <SelectItem key={listing.id} value={listing.id}>
                        {listing.business_name || "Untitled Business"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </>
              )}
            </Select>
            <Button
              type="button"
              onClick={handleAddBusinessListing}
              disabled={addingBusiness}
              variant="outline"
              className="h-11 rounded-full bg-white px-5 font-bold"
              data-testid="add-business-listing-btn"
            >
              {addingBusiness ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Add Business
            </Button>
          </div>
        </div>

        <Tabs value={dashboardTab} onValueChange={setDashboardTab} className="space-y-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <TabsList className="h-auto flex-wrap justify-start rounded-full bg-gray-100 p-1">
              <TabsTrigger value="listing" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">Manage My Listing</TabsTrigger>
              <TabsTrigger value="events" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">Events</TabsTrigger>
              <TabsTrigger value="email" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">Email List/Messages</TabsTrigger>
            </TabsList>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDashboardTab("announcements")}
              className={`h-11 w-fit rounded-full px-5 font-bold shadow-sm ${dashboardTab === "announcements" ? "border-[#1a1a1a] bg-[#1a1a1a] text-white hover:bg-[#1a1a1a] hover:text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="business-announcements-tab-button"
            >
              <Bell className="mr-2 h-4 w-4" /> Announcements
              {announcementUnreadCount > 0 && (
                <span className="ml-2 rounded-full bg-[#42D78A] px-2 py-0.5 text-xs font-black text-[#1a1a1a]">{announcementUnreadCount}</span>
              )}
            </Button>
          </div>

          <TabsContent value="listing" className="mt-0">
            <BusinessProfileForm
              data={business}
              onChange={handleChange}
              token={token}
              uploadEndpoint={`/api/upload/logo${selectedBusinessQuery}`}
              addressErrorIndexes={addressErrorIndexes}
              marketOptions={marketOptions}
              prefixSlot={<SubscriptionCard token={token} dashboardType="business" />}
            />
          </TabsContent>

          <TabsContent value="events" className="mt-0">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="my-events-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg"><CalendarDays className="w-5 h-5 text-[#42D78A]" /> My Events</CardTitle>
                <Button onClick={() => { setEditingEvent(null); setShowEventForm(true); }} className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-5 py-2 text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="biz-submit-event-btn">
                  <Plus className="w-4 h-4 mr-1" /> Submit Event
                </Button>
              </CardHeader>
              <CardContent>
                {myEvents.length === 0 ? <p className="text-gray-500 text-sm py-4">No events submitted yet. Submit one for the community!</p> : (
                  <div className="space-y-3">
                    {myEvents.map((event) => (
                      <div key={event.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl">
                        <div className="min-w-0 flex-1 mr-4">
                          <p className="font-medium text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{event.title}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {event.date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{event.date}</span>}
                            {formatEventTimeRange(event) && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatEventTimeRange(event)}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={event.approval_status === "pending" ? "bg-yellow-100 text-yellow-800" : event.approval_status === "rejected" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>{event.approval_status}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingEvent(event); setShowEventForm(true); }} className="text-slate-600 hover:text-slate-900 h-8 w-8 p-0" data-testid={`edit-event-${event.id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(event.id)} disabled={deletingEventId === event.id} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" data-testid={`delete-event-${event.id}`}>{deletingEventId === event.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email" className="mt-0">
            <div className="relative space-y-6 overflow-hidden rounded-2xl">
              {!emailAccessEnabled && <EmailBetaOverlay testId="business-email-beta-overlay" />}
              <Card className="bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="business-email-list-card">
                <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Mail className="w-5 h-5 text-[#42D78A]" /> Email List/Messages</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  <p className="text-sm font-medium">This feature is in BETA and there may be bugs.</p>
                </div>
                <div className="flex flex-col gap-3 rounded-xl bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{emailList?.name || "Your shopper list"}</p>
                    <p className="text-sm text-slate-500">Follower emails stay private. You can only see the count.</p>
                  </div>
                  <Badge className="w-fit bg-[#42D78A]/15 px-3 py-1 text-[#157347]"><Users className="mr-1.5 h-3.5 w-3.5" /> {emailList?.active_member_count || 0} followers</Badge>
                </div>
                <div className={!emailAccessEnabled ? "pointer-events-none select-none opacity-30" : ""} aria-hidden={!emailAccessEnabled}>
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,0.8fr)]">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-bold text-slate-700">Subject</label>
                      <Input value={emailSubject} onChange={(event) => setEmailSubject(event.target.value)} placeholder="What should followers see in their inbox?" className="mt-2 h-11 rounded-xl" maxLength={180} />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700">Message</label>
                      <div className="mt-2">
                        <RichTextEditor value={emailMessage} onChange={setEmailMessage} testId="business-email-message-editor" imageUploadEndpoint="/api/upload/message-image" videoUploadEndpoint="/api/upload/message-video" token={token} />
                      </div>
                      <p className="mt-2 text-xs text-slate-500">Use the image button to upload an image or GIF.</p>
                    </div>
                    {emailDraft?.updated_at && (
                      <p className="text-xs text-slate-500">Draft saved {formatEmailLogDate(emailDraft.updated_at)}.</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={handleSaveEmailDraft} disabled={savingDraft || (!emailSubject.trim() && !emailMessage.trim())} variant="outline" className="rounded-full px-6 font-bold">
                        {savingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Draft
                      </Button>
                      {emailDraft?.updated_at && (
                        <Button onClick={handleDeleteEmailDraft} disabled={savingDraft} variant="ghost" className="rounded-full px-6 font-bold text-red-600 hover:bg-red-50 hover:text-red-700">
                          <Trash2 className="mr-2 h-4 w-4" /> Delete Draft
                        </Button>
                      )}
                      <Button onClick={handleSendTestMessage} disabled={sendingTestEmail || !emailSubject.trim() || !emailMessage.trim()} variant="outline" className="rounded-full px-6 font-bold">
                        {sendingTestEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send Test to Myself
                      </Button>
                      <Button onClick={handleSendMessage} disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()} className="rounded-full bg-[#42D78A] px-6 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]">
                        {sendingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send Message
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Preview</p>
                    <div className="rounded-xl bg-slate-50 p-5">
                      <div className="mb-5 rounded-2xl bg-black px-5 py-6 text-center">
                        <div className="font-black leading-tight tracking-wide text-white" style={{ fontFamily: "'Outfit', sans-serif", fontSize: "28px" }}>
                          <div>614</div>
                          <div>SHOP</div>
                          <div>LOCAL</div>
                        </div>
                      </div>
                      <h3 className="mb-3 text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{emailSubject || "Your subject will appear here"}</h3>
                      <p className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Message from {business.business_name || "your business"}</p>
                      {emailMessage ? (
                        <div className="prose prose-sm max-w-none text-slate-600 [&_a]:font-bold [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1" dangerouslySetInnerHTML={{ __html: emailMessage }} />
                      ) : (
                        <p className="text-sm leading-relaxed text-slate-600">Your message preview will appear here.</p>
                      )}
                      <div className="mt-5 flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4">
                        {getLogoUrl(business.logo) ? (
                          <img src={getLogoUrl(business.logo)} alt={`${business.business_name || "Business"} logo`} className="h-14 w-14 rounded-2xl object-cover" />
                        ) : (
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#42D78A] text-2xl font-black text-[#1a1a1a]">
                            {(business.business_name || "B").slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Sent by</p>
                          <p className="truncate text-lg font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{business.business_name || "Your business"}</p>
                        </div>
                      </div>
                      <p className="mt-5 border-t border-slate-200 pt-4 text-xs text-slate-400">This is an automated message from 614 Shop Local.</p>
                    </div>
                  </div>
                </div>
                </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="business-email-log-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="w-5 h-5 text-[#42D78A]" /> Email Log
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {emailLogs.length === 0 ? (
                    <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No emails sent yet.</p>
                  ) : emailLogs.map((log) => (
                    <button
                      key={log.id}
                      type="button"
                      onClick={() => setSelectedEmailLog(log)}
                      className="grid w-full grid-cols-1 gap-3 rounded-xl bg-slate-50 p-4 text-left transition-all hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-sm md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
                      data-testid={`business-email-log-${log.id}`}
                    >
                      <div className="min-w-0">
                        <p className="truncate font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{log.subject || log.template_name || "Untitled email"}</p>
                        <p className="mt-1 text-sm text-slate-500">{formatEmailLogDate(log.created_at)} · Click to view</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-white text-slate-700 shadow-sm">{log.success_count || 0}/{log.recipient_count || 0} sent</Badge>
                        <Badge className={log.status === "sent" ? "bg-[#42D78A]/15 text-[#157347]" : log.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}>
                          {log.status || "unknown"}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="announcements" className="mt-0">
            <AnnouncementsPanel token={token} onUnreadCountChange={setAnnouncementUnreadCount} testId="business-announcements-panel" />
          </TabsContent>
        </Tabs>

        <EventFormModal open={showEventForm} onClose={() => { setShowEventForm(false); setEditingEvent(null); }} token={token} onSuccess={fetchMyEvents} editEvent={editingEvent} hostId={selectedBusinessId} />
        <EmailLogDialog log={selectedEmailLog} open={!!selectedEmailLog} onOpenChange={(open) => !open && setSelectedEmailLog(null)} />
      </main>
    </div>
  );
};

export default DashboardPage;
